import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { Upload, X, Save } from "lucide-react";

const BEREICHE = ["Brand", "Digital", "Print"];

export default function DesignerProfil() {
  const t = useT();
  const { refreshUser } = useAuth();
  const [d, setD] = useState(null);
  const [katalog, setKatalog] = useState({ leistungen: {}, tools: [] });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    (async () => {
      const [me, k] = await Promise.all([api.get("/designer"), api.get("/katalog")]);
      setD(me.data?.[0] || null);
      setKatalog(k.data);
    })();
  }, []);

  const upd = (patch) => setD((s) => ({ ...s, ...patch }));

  const toggleArr = (key, val) => setD((s) => {
    const cur = new Set(s[key] || []);
    if (cur.has(val)) cur.delete(val); else cur.add(val);
    return { ...s, [key]: [...cur] };
  });

  const handleFoto = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const r = new FileReader();
    r.onload = () => upd({ foto: r.result });
    r.readAsDataURL(f);
  };

  const addRef = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const r = new FileReader();
    r.onload = () => upd({ referenzbilder: [...(d.referenzbilder || []), r.result] });
    r.readAsDataURL(f);
    e.target.value = "";
  };

  const removeRef = (i) => upd({ referenzbilder: (d.referenzbilder || []).filter((_, idx) => idx !== i) });

  const save = async () => {
    if (!d) return;
    setBusy(true); setMsg("");
    try {
      const payload = {
        name: d.name, email: d.email, telefon: d.telefon || "",
        geburtsjahr: d.geburtsjahr || null, adresse: d.adresse || "",
        portfolio_link: d.portfolio_link || "", foto: d.foto || "",
        stundensatz_standard: parseFloat(d.stundensatz_standard) || 0,
        status: d.status,
        bereiche: d.bereiche || [], leistungen: d.leistungen || [],
        tools: d.tools || [], referenzbilder: d.referenzbilder || [],
        sprache: d.sprache || "de",
      };
      await api.put(`/designer/${d.id}`, payload);
      // Refresh auth user so language (sprache) etc. is applied portal-wide
      await refreshUser?.();
      setMsg(t("profile.saved"));
    } catch (e) {
      setMsg(t("profile.save_error"));
    } finally { setBusy(false); }
  };

  if (!d) return <div className="mono-label">{t("common.loading")}</div>;

  const leistungenAvailable = Object.entries(katalog.leistungen || {});

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="mono-label" style={{ color: "#BE2E6C", opacity: 1 }}>{t("profile.role")}</div>
          <h1 className="font-archivo font-bold text-4xl mt-1">{t("profile.title")}</h1>
        </div>
        <button onClick={save} disabled={busy} data-testid="profil-save" className="inline-flex items-center gap-2 px-4 py-2.5 bg-ink text-white rounded-sm hover:bg-black disabled:opacity-60">
          <Save size={14} /> {busy ? t("common.saving") : t("common.save")}
        </button>
      </div>

      {msg && <div className="mb-4 px-4 py-2 rounded-sm text-sm border" style={{
        color: "#157A4A", backgroundColor: "#DDEEE3", borderColor: "#157A4A44"
      }}>{msg}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Header */}
        <div className="paper-card p-6 lg:col-span-3">
          <div className="flex gap-6 items-start">
            <div className="shrink-0">
              <div className="w-28 h-28 rounded-sm border border-line overflow-hidden bg-paper flex items-center justify-center">
                {d.foto ? <img src={d.foto} alt="Foto" className="w-full h-full object-cover" /> : <span className="mono-label">—</span>}
              </div>
              <label className="mt-2 inline-flex items-center gap-1 text-xs cursor-pointer text-brunnerBlue hover:underline">
                <Upload size={11} /> {t("profile.photo_change")}
                <input type="file" accept="image/*" onChange={handleFoto} className="hidden" data-testid="profil-foto" />
              </label>
            </div>
            <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="mono-label block mb-1">{t("profile.name")}</label>
                <input value={d.name || ""} onChange={(e) => upd({ name: e.target.value })} className="w-full px-3 py-2 border border-line rounded-sm bg-white" />
              </div>
              <div>
                <label className="mono-label block mb-1">{t("profile.rate")}</label>
                <input type="number" step="0.5" value={d.stundensatz_standard || ""} onChange={(e) => upd({ stundensatz_standard: e.target.value })} data-testid="profil-satz" className="w-full px-3 py-2 border border-line rounded-sm bg-white" />
              </div>
              <div>
                <label className="mono-label block mb-1">{t("profile.email")}</label>
                <input value={d.email || ""} disabled className="w-full px-3 py-2 border border-line rounded-sm bg-paper" />
              </div>
              <div>
                <label className="mono-label block mb-1">{t("profile.phone")}</label>
                <input value={d.telefon || ""} onChange={(e) => upd({ telefon: e.target.value })} className="w-full px-3 py-2 border border-line rounded-sm bg-white" />
              </div>
              <div>
                <label className="mono-label block mb-1">{t("profile.birth_year")}</label>
                <input type="number" value={d.geburtsjahr || ""} onChange={(e) => upd({ geburtsjahr: parseInt(e.target.value, 10) || null })} className="w-full px-3 py-2 border border-line rounded-sm bg-white" />
              </div>
              <div>
                <label className="mono-label block mb-1">{t("profile.address")}</label>
                <input value={d.adresse || ""} onChange={(e) => upd({ adresse: e.target.value })} className="w-full px-3 py-2 border border-line rounded-sm bg-white" />
              </div>
              <div className="md:col-span-2">
                <label className="mono-label block mb-1">{t("profile.portfolio")}</label>
                <input value={d.portfolio_link || ""} onChange={(e) => upd({ portfolio_link: e.target.value })} className="w-full px-3 py-2 border border-line rounded-sm bg-white" placeholder="https://…" />
              </div>
              <div>
                <label className="mono-label block mb-1">{t("profile.language")}</label>
                <select value={d.sprache || "de"} onChange={(e) => upd({ sprache: e.target.value })} data-testid="profil-sprache" className="w-full px-3 py-2 border border-line rounded-sm bg-white">
                  <option value="de">Deutsch</option>
                  <option value="en">English</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Bereiche */}
        <div className="paper-card p-6 lg:col-span-3">
          <h3 className="font-archivo font-bold text-lg mb-1">{t("profile.areas")}</h3>
          <div className="mono-label mb-3">{t("profile.areas_hint")}</div>
          <div className="flex flex-wrap gap-2">
            {BEREICHE.map((b) => {
              const on = (d.bereiche || []).includes(b);
              return (
                <button key={b} type="button" onClick={() => toggleArr("bereiche", b)}
                  data-testid={`bereich-${b}`}
                  className={`px-3 py-1.5 rounded-sm text-sm border transition-colors ${on ? "bg-ink text-white border-ink" : "bg-white border-line hover:border-ink"}`}>
                  {b}
                </button>
              );
            })}
          </div>
        </div>

        {/* Leistungen */}
        <div className="paper-card p-6 lg:col-span-2">
          <h3 className="font-archivo font-bold text-lg mb-1">{t("profile.services")}</h3>
          <div className="mono-label mb-3">{t("profile.services_hint")}</div>
          {leistungenAvailable.length === 0 && <div className="text-sm text-ink/50">—</div>}
          {leistungenAvailable.map(([bereich, arr]) => (
            <div key={bereich} className="mb-4">
              <div className="mono-label mb-2" style={{opacity: 1}}>{bereich}</div>
              <div className="flex flex-wrap gap-1.5">
                {arr.map((l) => {
                  const on = (d.leistungen || []).includes(l);
                  return (
                    <button key={l} type="button" onClick={() => toggleArr("leistungen", l)}
                      className={`text-xs px-2.5 py-1 rounded-sm border ${on ? "bg-ink text-white border-ink" : "bg-white border-line hover:border-ink"}`}>
                      {l}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Tools */}
        <div className="paper-card p-6 lg:col-span-1">
          <h3 className="font-archivo font-bold text-lg mb-1">{t("profile.tools")}</h3>
          <div className="mono-label mb-3">{t("profile.tools_hint")}</div>
          <div className="flex flex-wrap gap-1.5">
            {(katalog.tools || []).map((tool) => {
              const on = (d.tools || []).includes(tool);
              return (
                <button key={tool} type="button" onClick={() => toggleArr("tools", tool)}
                  className={`text-xs px-2.5 py-1 rounded-sm border ${on ? "bg-ink text-white border-ink" : "bg-white border-line hover:border-ink"}`}>
                  {tool}
                </button>
              );
            })}
          </div>
        </div>

        {/* Referenzen */}
        <div className="paper-card p-6 lg:col-span-3">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-archivo font-bold text-lg">{t("profile.references")}</h3>
              <div className="mono-label mt-1">{t("profile.references_hint")}</div>
            </div>
            <label className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-line rounded-sm text-xs cursor-pointer hover:border-ink">
              <Upload size={11} /> {t("profile.add_ref")}
              <input type="file" accept="image/*" onChange={addRef} className="hidden" data-testid="profil-ref-add" />
            </label>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
            {(d.referenzbilder || []).map((src, i) => (
              <div key={i} className="relative group">
                <img src={src} alt="ref" className="w-full h-24 object-cover rounded-sm border border-line" />
                <button onClick={() => removeRef(i)} className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`profil-ref-del-${i}`}>
                  <X size={11} />
                </button>
              </div>
            ))}
            {(!d.referenzbilder || d.referenzbilder.length === 0) && <div className="text-sm text-ink/50 col-span-full">—</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
